import React, { useState, useEffect } from "react";
import "./balagruhamanagement.css";
import {
  getBalagruha,
  addBalagruha,
  updateBalagruha,
  deleteBalagruha,
  getMachines,
  assignMachineToAnotherBalagruha,
  getUnAssigned,
} from "../../api";
import { usePermission } from "../../hooks/usePermission";
import { api } from "../../api/client";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Baby, Camera, Check, ChevronDown, ChevronLeft, ChevronRight, CircleHelp, Clock3, ExternalLink, FileImage, Flower2, House, Link2, MapPin, Monitor, Phone, Plus, Ruler, Search, Settings2, Trash2, Users, UsersRound } from "lucide-react";

const BALAGRUHAS_PER_PAGE = 8;
const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// One day, or a From-To range ("Monday – Wednesday"): value is the From day, toDay the optional To day.
const DayDropdown = ({ value, toDay, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const menuRef = React.useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    // The form columns scroll without a visible scrollbar, so bring the whole picker into view.
    menuRef.current?.scrollIntoView?.({ block: "nearest" });
    const closeOnOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const fromIndex = DAYS_OF_WEEK.indexOf(value);
  const fullLabel = value ? (toDay ? `${value} – ${toDay}` : value) : "Select Day";
  // A range is shown short ("Wed – Sat") so it fits the Day cell; the tooltip has the full names.
  const label = value && toDay ? `${value.slice(0, 3)} – ${toDay.slice(0, 3)}` : fullLabel;

  return (
    <div className="day-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`day-dropdown-trigger${value ? " has-value" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={value ? fullLabel : undefined}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{label}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {isOpen && (
        <div className="day-dropdown-menu day-range-menu" ref={menuRef}>
          <div className="day-range-columns">
            <div className="day-range-column" role="listbox" aria-label="From day">
              <span className="day-range-heading">From</span>
              {DAYS_OF_WEEK.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  role="option"
                  aria-selected={value === day}
                  className={value === day ? "selected" : ""}
                  // Keeps the To day only while it is still later than the new From day.
                  onClick={() => onChange({ day, toDay: DAYS_OF_WEEK.indexOf(toDay) > index ? toDay : "" })}
                >
                  {day}
                </button>
              ))}
            </div>
            <div className="day-range-column" role="listbox" aria-label="To day (optional)">
              <span className="day-range-heading">To <small>(optional)</small></span>
              {DAYS_OF_WEEK.map((day, index) => (
                <button
                  key={day}
                  type="button"
                  role="option"
                  aria-selected={toDay === day}
                  className={toDay === day ? "selected" : ""}
                  disabled={fromIndex < 0 || index <= fromIndex}
                  // Picking the selected To day again turns the range back into a single day.
                  onClick={() => {
                    onChange({ day: value, toDay: toDay === day ? "" : day });
                    setIsOpen(false);
                  }}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="day-range-done" onClick={() => setIsOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
};

const TimeDropdown = ({ value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [validationMessage, setValidationMessage] = useState("");
  const dropdownRef = React.useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const toggleTimeInputs = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    const [savedHour = "", savedMinute = ""] = value ? value.split(":") : [];
    const hour24 = Number(savedHour);
    setHour(value ? String(hour24) : "");
    setMinute(savedMinute);
    setValidationMessage("");
    setIsOpen(true);
  };

  const applyTime = () => {
    const isValidHour = /^\d{1,2}$/.test(hour) && Number(hour) >= 0 && Number(hour) <= 23;
    const isValidMinute = /^\d{1,2}$/.test(minute) && Number(minute) >= 0 && Number(minute) <= 59;
    if (!isValidHour || !isValidMinute) {
      setValidationMessage("Enter an hour from 00 to 23 and minutes from 00 to 59.");
      return;
    }
    onChange(`${String(Number(hour)).padStart(2, "0")}:${String(Number(minute)).padStart(2, "0")}`);
    setIsOpen(false);
  };

  const displayTime = value ? (() => {
    const [savedHour, savedMinute] = value.split(":");
    return `${savedHour}:${savedMinute}`;
  })() : "Select time";

  return (
    <div className="time-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`time-dropdown-trigger${value ? " has-value" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={label}
        onClick={toggleTimeInputs}
      >
        <Clock3 aria-hidden="true" />
        <span>{displayTime}</span>
        <ChevronDown aria-hidden="true" className="time-dropdown-chevron" />
      </button>
      {isOpen && (
        <div className="time-input-panel" aria-label={`${label} entry`}>
          <label>
            <span>Hour</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="2"
              placeholder="HH"
              aria-label={`${label} hour`}
              value={hour}
              onChange={(event) => setHour(event.target.value.replace(/\D/g, "").slice(0, 2))}
              onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyTime(); } }}
            />
          </label>
          <span className="time-input-separator">:</span>
          <label>
            <span>Minute</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="2"
              placeholder="MM"
              aria-label={`${label} minute`}
              value={minute}
              onChange={(event) => setMinute(event.target.value.replace(/\D/g, "").slice(0, 2))}
              onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); applyTime(); } }}
            />
          </label>
          <button type="button" className="apply-time-button" onClick={applyTime}>Apply</button>
          {validationMessage && <small className="time-validation-message" role="alert">{validationMessage}</small>}
        </div>
      )}
    </div>
  );
};

const BalagruhaManagement = () => {
  const createEmptyFormData = () => ({
    name: "",
    location: "",
    assignedMachines: [],
    postalLocation: "",
    googleMapLink: "",
    boysCount: "",
    girlsCount: "",
    balagruhaCount: "",
    caretakerName: "",
    caretakerPhone: "",
    isfCount: "",
    distanceFromISF: "",
    classTimings: [{ day: "", startTime: "", endTime: "" }],
    infrastructureMaterials: [{ name: "", quantity: "" }],
  });
  const navigate = useNavigate();
  const [balagruhas, setBalagruhas] = useState([]);
  const [machines, setMachines] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'update'
  const [selectedBalagruha, setSelectedBalagruha] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [formData, setFormData] = useState(createEmptyFormData);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [metrics, setMetrics] = useState({
    totalBalagruhas: 0,
    totalMachines: 0,
    assignedMachines: 0,
    unassignedMachines: 0,
  });

  const { canCreate, canRead, canUpdate, canDelete } = usePermission();
  const [unassigned, setUnassigned] = useState([]);

  const canCreateBalagruha = canCreate("Balagruha Management");
  const canReadBalagruha = canRead("Balagruha Management");
  const canUpdateBalagruha = canUpdate("Balagruha Management");
  const canDeleteBalagruha = canDelete("Balagruha Management");

  useEffect(() => {
    getBalagruhas();
    fetchMachines();
    fetchPeople();
  }, []);

  const fetchPeople = async () => {
    try {
      const [studentResponse, staffResponse] = await Promise.all([
        api.get("/api/users", { params: { role: "student", limit: 1000 } }),
        api.get("/api/users", { params: { limit: 1000 } }),
      ]);
      const unwrap = (response) => {
        const data = response?.data?.data || response?.data;
        return Array.isArray(data) ? data : data?.users || data?.students || data?.results || [];
      };
      setStudents(unwrap(studentResponse));
      setStaff(unwrap(staffResponse).filter((person) => person.role !== "student"));
    } catch (error) {
      console.warn("Could not load Balagruha people details", error);
    }
  };

  useEffect(() => {
    calculateMetrics();
  }, [balagruhas, machines]);

  const getBalagruhas = async () => {
    try {
      const response = await getBalagruha();

      setBalagruhas(response?.data?.balagruhas || []);
    } catch (error) {
      console.error("Error fetching balagruhas:", error);
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await getMachines();

      setMachines(response?.data?.machines || []);
      getUnAssignedMachines();
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  const getUnAssignedMachines = async () => {
    try {
      const response = await getUnAssigned();

      setUnassigned(response.data.machines || []);
    } catch (error) {
      console.error("Erro in fetching machines", error);
    }
  };

  const calculateMetrics = () => {
    // Count total balagruhas
    const totalBalagruhas = balagruhas.length;

    // Count total machines
    const totalMachines = machines.length;

    // Count assigned machines (machines that are assigned to any balagruha)
    const assignedMachinesCount = machines.filter(
      (machine) =>
        machine.assignedBalagruha &&
        Object.keys(machine.assignedBalagruha).length > 0
    ).length;

    // Count unassigned machines
    const unassignedMachinesCount = totalMachines - assignedMachinesCount;

    setMetrics({
      totalBalagruhas,
      totalMachines,
      assignedMachines: assignedMachinesCount,
      unassignedMachines: unassignedMachinesCount,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    setFormErrors((old) => ({ ...old, [name]: null, submit: null }));
  };

  const updateClassTiming = (index, changes) => {
    setFormErrors((old) => ({ ...old, submit: null }));
    setFormData((old) => {
      const rows = old.classTimings?.length ? old.classTimings : [{ day: "", startTime: "", endTime: "" }];
      return { ...old, classTimings: rows.map((row, rowIndex) => rowIndex === index ? { ...row, ...changes } : row) };
    });
  };

  const getSubmitErrorMessage = (error) => {
    const message = String(error.response?.data?.message || "");
    if (/E11000|duplicate key/i.test(message)) {
      return "A Balagruha with this name already exists. Please choose a different name.";
    }
    if (/LIMIT_FILE_SIZE|file too large/i.test(message)) {
      return "A selected file is too large. Please choose files under 10 MB each.";
    }
    if (!error.response) {
      return "Unable to reach the server. Check your internet connection and try again.";
    }
    if (error.response.status >= 500) {
      return "We couldn’t save this Balagruha right now. Please try again in a few minutes.";
    }
    return "We couldn’t save this Balagruha. Please check the entered details and try again.";
  };

  const handleMachineSelection = (selectedMachine) => {
    const selectedMachineId =
      typeof selectedMachine === "object" ? selectedMachine._id : selectedMachine;

    const machineObj =
      machines.find((m) => m._id === selectedMachineId) ||
      unassigned.find((m) => m._id === selectedMachineId);

    setFormData((prevState) => {
      const isAlreadySelected = prevState.assignedMachines.some(
        (m) => String(typeof m === "object" ? m._id : m) === String(selectedMachineId)
      );

      const updatedMachines = isAlreadySelected
        ? prevState.assignedMachines.filter(
            (m) => String(typeof m === "object" ? m._id : m) !== String(selectedMachineId)
        )
        : [...prevState.assignedMachines, selectedMachineId];

      if (isAlreadySelected && machineObj) {
        setUnassigned((prev) => {
          const alreadyExists = prev.some((m) => m._id === selectedMachineId);
          return alreadyExists ? prev : [...prev, machineObj];
        });
      } else {
        setUnassigned((prev) =>
          prev.filter((m) => m._id !== selectedMachineId)
        );
      }

      return {
        ...prevState,
        assignedMachines: updatedMachines,
      };
    });
  };

  const validateForm = () => {
    const errors = {};
    const missingFields = [];
    const invalidFields = [];

    if (!formData.name.trim()) {
      errors.name = "Name is required";
      missingFields.push("Name");
    }

    if (!formData.location.trim()) {
      errors.location = "Location is required";
      missingFields.push("Postal Location");
    }

    // Boys, Girls and the Computer Count (ISF and Balagruha) are optional: blank or 0 is fine, but a
    // typed value must still be a valid count.
    [["boysCount", "Boys Count"], ["girlsCount", "Girls Count"], ["isfCount", "ISF Count"], ["balagruhaCount", "Balagruha Count"]].forEach(([field, label]) => {
      const value = formData[field];
      if (value !== "" && value != null && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
        errors[field] = "Enter a valid non-negative count";
        invalidFields.push(label);
      }
    });

    if (!formData.caretakerName.trim()) {
      errors.caretakerName = "Caretaker name is required";
      missingFields.push("Caretaker Name");
    }
    if (!formData.caretakerPhone.trim()) {
      errors.caretakerPhone = "Phone number is required";
      missingFields.push("Phone Number");
    } else if (!/^\d{10}$/.test(formData.caretakerPhone)) {
      errors.caretakerPhone = "Enter a valid 10-digit phone number";
      invalidFields.push("Phone Number (10 digits)");
    }

    if (formData.distanceFromISF === "" || formData.distanceFromISF == null) {
      errors.distanceFromISF = "Distance is required";
      missingFields.push("Distance from ISF Centre");
    } else if (!Number.isFinite(Number(formData.distanceFromISF)) || Number(formData.distanceFromISF) < 0) {
      errors.distanceFromISF = "Enter a valid non-negative distance";
      invalidFields.push("Distance from ISF Centre");
    }

    const timings = formData.classTimings || [];
    if (!timings.length || timings.some((timing) => !timing.day || !timing.startTime || !timing.endTime)) {
      errors.classTimings = "Complete the day, start time, and end time";
      missingFields.push("Available Class Timing (Day, Start Time, End Time)");
    }

    const summary = [
      missingFields.length ? `Please fill required fields: ${missingFields.join(", ")}.` : "",
      invalidFields.length ? `Please correct: ${invalidFields.join(", ")}.` : "",
    ].filter(Boolean).join(" ");

    setFormErrors({ ...errors, submit: summary || null });
    return !summary;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        ...formData,
        postalLocation: formData.location,
        totalChildren: Number(formData.boysCount || 0) + Number(formData.girlsCount || 0),
        assignedMachines: formData.assignedMachines.map((machine) => typeof machine === "object" ? machine._id : machine),
        infrastructureMaterials: formData.infrastructureMaterials.map((material) => ({ ...material, quantity: Number(material.quantity) || 0 })),
      };
      if (modalMode === "create") {
        const request = new FormData();
        Object.entries(payload).filter(([key]) => key !== "caretakerPhotoFile").forEach(([key, value]) => request.append(key, Array.isArray(value) || (value && typeof value === "object") ? JSON.stringify(value) : value));
        if (formData.caretakerPhotoFile) request.append("caretakerPhoto", formData.caretakerPhotoFile);
        documentFiles.forEach((file) => request.append("documents", file));
        await addBalagruha(request);

        setConfirmationMessage("Balagruha added successfully!");

      } else {
        const submissionData = new FormData();
        Object.entries(payload).filter(([key]) => key !== "caretakerPhotoFile").forEach(([key, value]) => submissionData.append(key, Array.isArray(value) || (value && typeof value === "object") ? JSON.stringify(value) : value));
        if (formData.caretakerPhotoFile) submissionData.append("caretakerPhoto", formData.caretakerPhotoFile);
        documentFiles.forEach((file) => submissionData.append("documents", file));

        const response = await updateBalagruha(
          selectedBalagruha._id,
          submissionData
        );

        setConfirmationMessage("Balagruha updated successfully!");

        // Handle machine assignments
        const currentAssignedMachines =
          selectedBalagruha.assignedMachines || [];

        // Machines to add (in formData but not in currentAssignedMachines)
        const machinesToAdd = formData.assignedMachines.filter(
          (machineId) => !currentAssignedMachines.includes(machineId)
        );

        // Machines to remove (in currentAssignedMachines but not in formData)
        const machinesToRemove = currentAssignedMachines.filter(
          (machineId) => !formData.assignedMachines.includes(machineId)
        );
      }

      setShowConfirmation(true);
      setShowModal(false);
      getBalagruhas();
      fetchMachines();
      getUnAssignedMachines();

      // Reset form
      setFormData({
        name: "",
        location: "",
        assignedMachines: [],
        postalLocation: "", googleMapLink: "", boysCount: "", girlsCount: "",
        balagruhaCount: "", caretakerName: "", caretakerPhone: "", isfCount: "", distanceFromISF: "",
        classTimings: [{ day: "", startTime: "", endTime: "" }], infrastructureMaterials: [{ name: "", quantity: "" }],
      });
      setDocumentFiles([]);

      setTimeout(() => {
        setShowConfirmation(false);
      }, 2000);
    } catch (error) {
      console.error("Error processing balagruha:", error);
      setFormErrors((old) => ({ ...old, submit: getSubmitErrorMessage(error) }));
    }
  };

  const handleDeleteBalagruha = async () => {
    if (selectedBalagruha) {
      try {
        const response = await deleteBalagruha(selectedBalagruha._id);


        setBalagruhas(
          balagruhas.filter((bg) => bg._id !== selectedBalagruha._id)
        );
        setShowDeleteModal(false);
        setConfirmationMessage("Balagruha deleted successfully!");
        setShowConfirmation(true);

        // Refresh machines data
        fetchMachines();

        setTimeout(() => {
          setShowConfirmation(false);
        }, 2000);
      } catch (error) {
        console.error("Error deleting balagruha:", error);
        setConfirmationMessage("Failed to delete balagruha. Please try again.");
        setShowConfirmation(true);
        setTimeout(() => {
          setShowConfirmation(false);
        }, 2000);
      }
    }
  };

  // Filter balagruhas based on search term
  const filteredBalagruhas = balagruhas.filter(
    (balagruha) =>
      String(balagruha.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(balagruha.location || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filteredBalagruhas.length / BALAGRUHAS_PER_PAGE));
  const pageSafe = Math.min(currentPage, totalPages);
  const paginatedBalagruhas = filteredBalagruhas.slice(
    (pageSafe - 1) * BALAGRUHAS_PER_PAGE,
    pageSafe * BALAGRUHAS_PER_PAGE
  );

  // Get machine name by ID
  const getBalagruhaStudents = (balagruha) => students.filter((student) =>
    (student.balagruhaIds || []).some((item) => String(item?._id || item) === String(balagruha._id))
  );
  const getPeopleCount = (balagruha, gender) => getBalagruhaStudents(balagruha).filter((student) =>
    String(student.gender || "").toLowerCase() === (gender === "boys" ? "male" : "female")
  ).length;
  const getCaretaker = (balagruha) => staff.find((person) =>
    (person.balagruhaIds || []).some((item) => String(item?._id || item) === String(balagruha._id))
  );

  const visibleClassTimings = formData.classTimings?.length
    ? formData.classTimings
    : [{ day: "", startTime: "", endTime: "" }];
  const visibleInfrastructureMaterials = formData.infrastructureMaterials?.length
    ? formData.infrastructureMaterials
    : [{ name: "", quantity: "" }];
  const googleMapsUrl = /^https?:\/\//i.test(formData.googleMapLink.trim())
    ? formData.googleMapLink.trim()
    : "";

  return (
    <div className="balagruha-management">
      <div className="balagruha-header">
        <div className="page-title"><span className="page-title-icon"><House aria-hidden="true" /></span><div><h2>Balagruha Management</h2><p>View and manage all Balagruhas and Varkari Sansthas</p></div></div>
        <div className="header-actions">
          <div className="search-box relative">
            <Search className="balagruha-search-icon" aria-hidden="true" />
            <input
              type="text"
              id="balagruha-search"
              placeholder="Search balagruhas..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="search-input balagruha-search-input"
              aria-label="Search balagruhas"
            />
          </div>

          {/* {canCreateBalagruha && ( */}
          <button type="button" className="add-machine-button" onClick={() => navigate("/machines", { state: { openAddMachine: true } })}>
            <Monitor aria-hidden="true" /> Add Machines
          </button>
          <button
            className="add-button"
            onClick={() => {
              setModalMode("create");
              setFormData(createEmptyFormData());
              setFormErrors({});
              setShowModal(true);
            }}
          >
            <><Plus aria-hidden="true" /> Add Balagruha</>
          </button>
          {/* )} */}
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="metrics-cards">
        <div className="metric-card">
          <div className="metric-icon"><House aria-hidden="true" /></div>
          <div className="metric-content">
            <div className="metric-value">{metrics.totalBalagruhas}</div>
            <div className="metric-label">Total Balagruhas</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><Monitor aria-hidden="true" /></div>
          <div className="metric-content">
            <div className="metric-value">{metrics.totalMachines}</div>
            <div className="metric-label">Total Computers</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><Check aria-hidden="true" /></div>
          <div className="metric-content">
            <div className="metric-value">{metrics.assignedMachines}</div>
            <div className="metric-label">Assigned Computers</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon"><CircleHelp aria-hidden="true" /></div>
          <div className="metric-content">
            <div className="metric-value">{metrics.unassignedMachines}</div>
            <div className="metric-label">Unassigned Computers</div>
          </div>
        </div>
      </div>

      <div className="balagruha-table-container">
        <table className="balagruha-table">
          <colgroup>
            <col className="col-number" /><col className="col-name" /><col className="col-location" />
            <col className="col-child" /><col className="col-child" /><col className="col-child-total" />
            <col className="col-caretaker" /><col className="col-computer" /><col className="col-computer-count" /><col className="col-actions" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan="2">Sl No</th>
              <th rowSpan="2">Balagruha Name</th>
              <th rowSpan="2">Location</th>
              <th className="group-heading" colSpan="3">Total Children</th>
              <th rowSpan="2">Caretaker Name<br /><span className="table-subheading">Contact No.</span></th>
              <th className="group-heading" colSpan="2">No. of Computers</th>
              <th rowSpan="2">Actions</th>
            </tr>
            <tr className="balagruha-subheadings">
              <th><span className="table-label-icon"><Users aria-hidden="true" /></span>Boys</th><th><span className="table-label-icon"><Users aria-hidden="true" /></span>Girls</th><th><span className="table-label-icon"><Users aria-hidden="true" /></span>Total</th>
              <th>ISF Count</th><th>Balagruha Count</th>
            </tr>
          </thead>
          <tbody>
            {paginatedBalagruhas.map((balagruha, index) => (
              <tr key={balagruha._id}>
                <td>{(pageSafe - 1) * BALAGRUHAS_PER_PAGE + index + 1}</td>
                <td>{balagruha.name}</td>
                <td><span className="location-name">{balagruha.location}</span>{balagruha.city && <span className="location-detail">{balagruha.city}{balagruha.state ? `, ${balagruha.state}` : ""}</span>}</td>
                <td><span className="count-pill boys-count">{balagruha.boysCount ?? getPeopleCount(balagruha, "boys")}</span></td>
                <td><span className="count-pill girls-count">{balagruha.girlsCount ?? getPeopleCount(balagruha, "girls")}</span></td>
                <td><span className="count-pill total-count">{balagruha.totalChildren ?? (Number(balagruha.boysCount ?? getPeopleCount(balagruha, "boys")) + Number(balagruha.girlsCount ?? getPeopleCount(balagruha, "girls")))}</span></td>
                <td>{(() => { const caretaker = getCaretaker(balagruha); const name = balagruha.caretakerName || caretaker?.name || "—"; const phone = balagruha.caretakerPhone || caretaker?.mobileNumber; return <><span className="caretaker-name">{name}</span>{phone && <span className="caretaker-phone"><Phone aria-hidden="true" />{phone}</span>}</>; })()}</td>
                <td><span className="count-pill isf-count">{balagruha.isfCount ?? 0}</span></td>
                <td><span className="count-pill bg-count">{balagruha.balagruhaCount ?? (balagruha.assignedMachines || []).length}</span></td>
                {/* {(canUpdateBalagruha || canDeleteBalagruha) && ( */}
                <td>
                  <div className="action-buttons">
                    {/* {canUpdateBalagruha && ( */}
                    <button
                      className="action-button edit"
                      onClick={() => {
                        const caretaker = getCaretaker(balagruha);
                        setSelectedBalagruha(balagruha);
                        setFormData({
                          ...createEmptyFormData(),
                          name: balagruha.name,
                          location: balagruha.location,
                          postalLocation: balagruha.postalLocation || "",
                          googleMapLink: balagruha.googleMapLink || "",
                          boysCount: Number(balagruha.boysCount) > 0 ? balagruha.boysCount : getPeopleCount(balagruha, "boys") || "",
                          girlsCount: Number(balagruha.girlsCount) > 0 ? balagruha.girlsCount : getPeopleCount(balagruha, "girls") || "",
                          balagruhaCount: Number(balagruha.balagruhaCount ?? (balagruha.assignedMachines || []).length) || "",
                          caretakerName: balagruha.caretakerName || caretaker?.name || "",
                          caretakerPhone: balagruha.caretakerPhone || caretaker?.mobileNumber || "",
                          isfCount: Number(balagruha.isfCount) || "",
                          distanceFromISF: balagruha.distanceFromISF || "",
                          classTimings: balagruha.classTimings?.length ? balagruha.classTimings : [{ day: "", startTime: "", endTime: "" }],
                          infrastructureMaterials: balagruha.infrastructureMaterials?.length ? balagruha.infrastructureMaterials.map((material) => ({ ...material, quantity: Number(material.quantity) || "" })) : [{ name: "", quantity: "" }],
                          assignedMachines: (balagruha.assignedMachines || []).map((machine) =>
                            typeof machine === "object" ? machine._id : machine
                          ),
                          caretakerPhoto: balagruha.caretakerPhoto || "",
                          documents: balagruha.documents || [],
                        });
                        setFormErrors({});
                        setModalMode("update");
                        setShowModal(true);
                      }}
                      title="Edit Balagruha"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="row-action-icon"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z"/></svg></button>
                    {/* )} */}

                    {/* {canDeleteBalagruha && ( */}
                    <button
                      className="action-button delete"
                      onClick={() => {
                        setSelectedBalagruha(balagruha);
                        setShowDeleteModal(true);
                      }}
                      title="Delete Balagruha"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" className="row-action-icon"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="m19 6-1 14H6L5 6"/><path d="M10 11v5M14 11v5"/></svg></button>
                    {/* )} */}
                  </div>
                </td>
                {/* )} */}
              </tr>
            ))}
          </tbody>
        </table>

        {filteredBalagruhas.length === 0 && (
          <div className="no-balagruhas">
            <div className="no-data-icon"><House aria-hidden="true" /></div>
            <div className="no-data-message">No balagruhas found</div>
          </div>
        )}
        <div className="balagruha-pagination">
          <span className="balagruha-pagination-info">
            {filteredBalagruhas.length === 0
              ? "Showing 0 Balagruhas"
              : `Showing ${(pageSafe - 1) * BALAGRUHAS_PER_PAGE + 1}–${Math.min(pageSafe * BALAGRUHAS_PER_PAGE, filteredBalagruhas.length)} of ${filteredBalagruhas.length} Balagruhas`}
          </span>
          {totalPages > 1 && (
            <div className="balagruha-pagination-controls" aria-label="Balagruha table pagination">
              <button type="button" aria-label="Previous page" disabled={pageSafe === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>
                <ChevronLeft aria-hidden="true" />
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button key={page} type="button" aria-current={pageSafe === page ? "page" : undefined} className={pageSafe === page ? "active" : ""} onClick={() => setCurrentPage(page)}>
                  {page}
                </button>
              ))}
              <button type="button" aria-label="Next page" disabled={pageSafe === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>
                <ChevronRight aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay balagruha-form-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) setShowModal(false); }}>
          <div className="modal-content balagruha-form-modal">
            <div className="modal-header balagruha-form-titlebar">
              <h3><span className="form-home-icon"><House aria-hidden="true" /></span>{modalMode === "create" ? "Add New Balagruha" : "Edit Balagruha"}</h3>
              <button type="button" className="close-button" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form noValidate onSubmit={handleSubmit} className={`balagruha-form-grid${modalMode === "update" ? " is-edit-mode" : ""}`}>
              <div className={`balagruha-form-columns${formErrors.submit ? " has-submit-error" : ""}`}>
                {formErrors.submit && <div className="form-submit-error" role="alert" aria-live="assertive"><span>{formErrors.submit}</span><button type="button" onClick={() => setFormErrors((old) => ({ ...old, submit: null }))} aria-label="Dismiss error">×</button></div>}
                <div className="balagruha-form-column balagruha-form-column-left">
              <section className="form-section-card section-name">
                <h4><span className="section-number">1</span>Name <span className="required">*</span></h4>
                <input id="name" name="name" placeholder="Enter Balagruha name" value={formData.name} onChange={handleInputChange} className={formErrors.name ? "error" : ""} required />
              </section>
              <section className="form-section-card section-children">
                <h4><span className="section-number blue-number">3</span>No. of Children</h4>
                <div className="child-count-grid">
                  <label className="child-count-box boys-box"><span><Baby aria-hidden="true" /> Boys</span><input type="number" min="0" name="boysCount" placeholder="0" value={formData.boysCount} onChange={handleInputChange} /></label>
                  <label className="child-count-box girls-box"><span><span className="girl-child-icon"><Baby aria-hidden="true" /><Flower2 aria-hidden="true" className="girl-child-marker" /></span> Girls</span><input type="number" min="0" name="girlsCount" placeholder="0" value={formData.girlsCount} onChange={handleInputChange} /></label>
                  <div className="child-count-box total-box"><span><UsersRound aria-hidden="true" /> Total Children</span><output>{Number(formData.boysCount || 0) + Number(formData.girlsCount || 0)}</output></div>
                </div>
              </section>
              <section className="form-section-card section-caretaker">
                <h4><span className="section-number">5</span>Caretaker Details</h4>
                <label>Caretaker Name <span className="required">*</span><input name="caretakerName" value={formData.caretakerName} onChange={handleInputChange} placeholder="Enter caretaker name" /></label>
                <label>Phone Number <span className="required">*</span><span className="form-input-icon"><Phone aria-hidden="true" /><input type="tel" name="caretakerPhone" value={formData.caretakerPhone} onChange={handleInputChange} placeholder="Enter 10-digit number" maxLength="10" /></span></label>
                <label className="upload-tile"><Camera aria-hidden="true" /><span>Upload Photo<br /><small>(Optional)</small></span><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setFormData((old) => ({ ...old, caretakerPhotoFile: event.target.files?.[0] || null }))} /></label>
                {formData.caretakerPhotoFile && <small className="selected-file-name" title={formData.caretakerPhotoFile.name}>{formData.caretakerPhotoFile.name}</small>}
              </section>
              <section className="form-section-card section-distance"><h4><span className="section-number green-number">7</span>Distance from ISF Centre <span className="required">*</span></h4><div className="distance-input"><Ruler aria-hidden="true" /><input type="number" min="0" step="0.1" name="distanceFromISF" value={formData.distanceFromISF} onChange={handleInputChange} placeholder="Enter distance" /><span>km</span></div></section>
              <section className="form-section-card section-documents"><h4><span className="section-number pink-number">9</span>Balagruha Photos / Documents</h4><label className="document-dropzone"><FileImage aria-hidden="true" className="document-upload-icon" /><strong>Upload Photos or PDF</strong><span>JPG, PNG, PDF (Max 10 MB each)</span><input type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(event) => setDocumentFiles(Array.from(event.target.files || []))} /></label><small className="upload-hint">You can upload multiple photos or documents.</small>{formData.documents?.length > 0 && <div className="existing-documents">{formData.documents.map((document, index) => <a key={`${document}-${index}`} href={document} target="_blank" rel="noopener noreferrer">{`Existing file ${index + 1}`}</a>)}</div>}{documentFiles.length > 0 && <small className="selected-file-name" title={documentFiles.map((file) => file.name).join(", ")}>{documentFiles.map((file) => file.name).join(", ")}</small>}</section>
                </div>
                <div className="balagruha-form-column balagruha-form-column-right">
                  <section className="form-section-card section-location">
                    <h4><span className="section-number">2</span>Location <span className="required">*</span></h4>
                    <div className="location-inputs">
                      <label>Postal Location <span className="required">*</span><span className="form-input-icon"><MapPin aria-hidden="true" /><input id="location" name="location" placeholder="Area, city, pincode" value={formData.location} onChange={handleInputChange} required /></span></label>
                      <label>Google Map Link<span className="form-input-icon"><Link2 aria-hidden="true" /><input type="url" name="googleMapLink" placeholder="Paste Google Maps link" value={formData.googleMapLink} onChange={handleInputChange} /><a className={`map-link-external${googleMapsUrl ? "" : " is-disabled"}`} href={googleMapsUrl || undefined} target="_blank" rel="noopener noreferrer" aria-label="Open Google Maps link in a new tab" title={googleMapsUrl ? "Open link in new tab" : "Enter a valid Google Maps link first"} tabIndex={googleMapsUrl ? 0 : -1}><ExternalLink aria-hidden="true" /></a></span></label>
                    </div>
                  </section>
                  <section className="form-section-card section-computers">
                    <h4><span className="section-number green-number">4</span>Computer Count</h4>
                    <div className="computer-count-grid"><label><span><Monitor aria-hidden="true" /> ISF Count (Optional)</span><input type="number" min="0" name="isfCount" placeholder="0" value={formData.isfCount} onChange={handleInputChange} /></label><label><span><Monitor aria-hidden="true" /> Balagruha Count (Optional)</span><input type="number" min="0" name="balagruhaCount" placeholder="0" value={formData.balagruhaCount} onChange={handleInputChange} /></label></div>
                    <details className="assign-machines-details"><summary><Settings2 aria-hidden="true" /><span>Assign Machines</span><ChevronRight aria-hidden="true" className="assign-machines-chevron" /></summary><div className="machine-selection">{machines.map((machine) => { const id = machine._id; const selected = formData.assignedMachines.some((item) => (typeof item === "object" ? item._id : item) === id); return <label className="machine-option" key={id}><input type="checkbox" checked={selected} onChange={() => handleMachineSelection(id)} />{machine.serialNumber}</label>; })}</div></details>
                  </section>
                  <section className="form-section-card section-timings">
                    <h4><span className="section-number blue-number">6</span>Available Class Timing <span className="required">*</span><button type="button" className="small-add-button" onClick={() => { setFormErrors((old) => ({ ...old, submit: null })); setFormData((old) => ({ ...old, classTimings: [...(old.classTimings || []), { day: "", startTime: "", endTime: "" }] })); }}><Plus aria-hidden="true" /> Add Timing</button></h4>
                    <div className="timing-table-header"><span>Day</span><span>Start Time</span><span>End Time</span><span>Actions</span></div>
                    {visibleClassTimings.map((timing, index) => <div className="timing-row" key={index}><DayDropdown value={timing.day} toDay={timing.toDay} onChange={(days) => updateClassTiming(index, days)} /><TimeDropdown label="Start time" value={timing.startTime} onChange={(startTime) => updateClassTiming(index, { startTime })} /><TimeDropdown label="End time" value={timing.endTime} onChange={(endTime) => updateClassTiming(index, { endTime })} /><button type="button" className="remove-row-button" aria-label="Remove timing" onClick={() => { setFormErrors((old) => ({ ...old, submit: null })); setFormData((old) => ({ ...old, classTimings: old.classTimings.filter((_, i) => i !== index) })); }}><Trash2 aria-hidden="true" /></button></div>)}
                  </section>
                  <section className="form-section-card section-materials">
                    <h4><span className="section-number pink-number">8</span>Infrastructure Material<button type="button" className="small-add-button" onClick={() => setFormData((old) => ({ ...old, infrastructureMaterials: [...old.infrastructureMaterials, { name: "", quantity: "" }] }))}><Plus aria-hidden="true" /> Add Material</button></h4>
                    <div className="material-table-header"><span>#</span><span>Material Name</span><span>Quantity</span><span>Actions</span></div>
                    {visibleInfrastructureMaterials.map((material, index) => <div className="material-row" key={index}><span>{index + 1}</span><input aria-label="Material name" placeholder="Enter material name" value={material.name} onChange={(event) => setFormData((old) => { const rows = old.infrastructureMaterials?.length ? old.infrastructureMaterials : [{ name: "", quantity: "" }]; return { ...old, infrastructureMaterials: rows.map((row, i) => i === index ? { ...row, name: event.target.value } : row) }; })} /><input type="number" min="0" aria-label="Quantity" placeholder="0" value={material.quantity} onChange={(event) => setFormData((old) => { const rows = old.infrastructureMaterials?.length ? old.infrastructureMaterials : [{ name: "", quantity: "" }]; return { ...old, infrastructureMaterials: rows.map((row, i) => i === index ? { ...row, quantity: event.target.value } : row) }; })} /><button type="button" className="remove-row-button" aria-label="Remove material" onClick={() => setFormData((old) => ({ ...old, infrastructureMaterials: old.infrastructureMaterials.filter((_, i) => i !== index) }))}><Trash2 aria-hidden="true" /></button></div>)}
                  </section>
                </div>
              </div>
              <div className="modal-actions balagruha-form-actions"><button type="submit" className="submit-button">{modalMode === "create" ? "Add Balagruha" : "Save Changes"}</button><button type="button" className="cancel-button" onClick={() => setShowModal(false)}>Cancel</button></div>
            </form>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <div className="modal-icon"><AlertTriangle aria-hidden="true" /></div>
              <h3>Confirm Deletion</h3>
              <button type="button" className="close-button" onClick={() => setShowDeleteModal(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the balagruha{" "}
                <strong>{selectedBalagruha.name}</strong>?
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button
                className="delete-confirm-button"
                onClick={handleDeleteBalagruha}
              >
                Yes, Delete
              </button>
              <button
                className="cancel-button"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Message */}
      {showConfirmation && (
        <div className="confirmation-message">
          <div className="confirmation-icon"><Check aria-hidden="true" /></div>
          <div className="confirmation-text">{confirmationMessage}</div>
        </div>
      )}
    </div>
  );
};

export default BalagruhaManagement;
